import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, DatePicker, message, Button, Table, Tooltip } from "antd";
import { all_routes } from "../../../router/all_routes";
import { addStudent, updateStudent, getStudent } from "../../../services/studentService";
import { CITIES_OPTIONS, GENDER_OPTIONS } from "../../../constants/userConstants";
import CommonSelect from "../../Common/CommonSelect";
import { parseSQLDate, formatToSQLDate } from "../../../utils/dateUtils";
import CommonFormLayout from "../../Common/CommonFormLayout";
import { GuardianRelationship, GuardianFamilySituation } from "../../../types/guardianTypes";
import { createGuardian } from "../../../services/guardianService";
import AddGuardianModal from "../guardian/components/AddGuardianModal";
import { useGuardians } from "../guardian/hooks/useGuardians";
import frFR from "antd/es/date-picker/locale/fr_FR";  // or "antd/locale/fr_FR" in v5+
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { getClasses } from "../../../services/classService";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { isValidMassarCode, isValidPhoneNumber } from "utils/validators";
import { useAbility } from "config/abilityContext";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { useSelector } from "react-redux";
import CustomTooltip from "components/Common/ui/CustomTooltip";

dayjs.locale("fr");

const StudentForm = () => {
  type LinkedGuardianInput = {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    relationship: GuardianRelationship;
    familySituation: GuardianFamilySituation;
    existingGuardianId?: string;
    studentIds?: string[];
  };

  const { t } = useTranslation();
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { workSpaces, user } = useSelector((state: any) => state.authSlice);
  const currentWorkspaceName = useMemo(() => {
    return workSpaces.find((workspace: any) => workspace.id === user?.instanceId)?.name;
  }, [workSpaces, user]);
  const studentId = id ?? "";
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [linkedGuardians, setLinkedGuardians] = useState<LinkedGuardianInput[]>([]); // Guardians to be submitted with the form
  const [displayedGuardians, setDisplayedGuardians] = useState<LinkedGuardianInput[]>([]); // Guardians to display, especially in edit mode
  const [addModalOpen, setAddModalOpen] = useState(false);
  const translatedGender = GENDER_OPTIONS.map(gender => ({
    ...gender,
    label: t(gender.label), // Translate dynamically
  }));
  const { linkStudents } = useGuardians();
  const ability = useAbility();
  // const [classes, setClasses] = useState<{ value: string; label: string }[]>([]); // Removed classes state

  useEffect(() => {
    // fetchClasses(); // Removed call to fetchClasses
    if (studentId) {
      setIsEdit(true);
      fetchStudentData(studentId);
    }
  }, [studentId]);

  const fetchStudentData = async (id: string) => {
    try {
      const response = await getStudent(id);

      const initialData = {
        user: {
          firstName: response.data.user?.firstName || "",
          lastName: response.data.user?.lastName || "",
          email: response.data.user?.email || "",
          phoneNumber: response.data.user?.phoneNumber || "",
          birthDate: response.data.user?.birthDate ? parseSQLDate(response.data.user.birthDate) : null,
          gender: response.data.user?.gender || null, // ✅ Ensure gender is correctly assigned
          cityOfBirth: CITIES_OPTIONS.find((city) => city.name === response.data.user?.cityOfBirth)?.name || "",
          zipCode: response.data.user?.zipCode || "",
          address: response.data.user?.address || "",
          city: CITIES_OPTIONS.find((city) => city.name === response.data.user?.city)?.name || "",
        },
        massarCode: response.data.massarCode || ""
        // classId:response?.data?.classId ?? "" // Removed classId from initialData
      };
      const guardianList = response.data.linkedGuardians?.map((link: any) => ({
        email: link.guardian.user.email,
        firstName: link.guardian.user.firstName,
        lastName: link.guardian.user.lastName,
        phoneNumber: link.guardian.user.phoneNumber,
        relationship: link.relationship ?? "guardian",
        familySituation: link.familySituation ?? "SINGLE",
      })) ?? [];

      // For existing students, populate displayedGuardians and clear linkedGuardians (for submission)
      setDisplayedGuardians(guardianList);
      setLinkedGuardians([]); // Ensures only newly added guardians are in the submission list for edits
      form.setFieldsValue(initialData);

      // ✅ Force a state update to ensure form values are available before rendering
      setTimeout(() => {
        form.setFieldsValue({ user: { ...initialData.user } });
      }, 0);
    } catch (error) {
      message.error(t("failed_to_load_student_data"));
    }
  };

  // const fetchClasses = async () => { // Removed fetchClasses function
  //   try {
  //     const response = await getClasses();
  //     setClasses(response.map((classItem: any) => ({ value: classItem.id, label: classItem.name })));
  //   } catch (error) {
  //     message.error(t("failed_to_load_classes"));
  //   }
  // };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        user: {
          ...values.user,
          birthDate: values.user.birthDate ? formatToSQLDate(values.user.birthDate) : null,
        },
        linkedGuardians: linkedGuardians, // Add linked guardians to the payload
      };

      let finalStudentId = studentId;
      if (!isEdit) {
        // Create student
        const response = await addStudent(payload);
        finalStudentId = response.data?.id;
        message.success(t("student_added_successfully"));
      } else {
        // Update existing
        await updateStudent(studentId, payload);
        message.success(t("student_updated_successfully"));
      }

      // ✅ Link guardians appropriately
      for (const guardian of linkedGuardians) {
        const existingIds = guardian.studentIds || [];
        const allIds = Array.from(new Set([...existingIds, finalStudentId]));

        if (guardian.existingGuardianId) {
          await linkStudents(guardian.existingGuardianId, allIds, guardian.relationship);
        } else {
          await createGuardian({
            user: {
              email: guardian.email,
              firstName: guardian.firstName,
              lastName: guardian.lastName,
              phoneNumber: guardian.phoneNumber,
            },
            relationship: guardian.relationship,
            familySituation: guardian.familySituation,
            studentIds: allIds,
          });
        }
      }

      navigate(routes.studentList);
    } catch (error) {
      message.error(t("failed_to_save_student_data"));
    }
  };


  const generateEmail = () => {
    const formValues = form.getFieldsValue();
    const userData = formValues.user || {};

    let firstName = (userData.firstName || "").trim().toLowerCase();
    let lastName = (userData.lastName || "").trim().toLowerCase();
    let prefix = (currentWorkspaceName ?? "").trim().toLowerCase();

    if (!firstName) {
      message.warning(t("first_name_required"));
      return;
    }

    if (!lastName) {
      message.warning(t("last_name_required"));
      return;
    }

    firstName = firstName.replace(/[^a-z]/g, '');
    lastName = lastName.replace(/[^a-z]/g, '');
    prefix = prefix.replace(/[^a-z]/g, '');


    const generatedEmail = `${firstName}.${lastName}@${prefix}.com`;

    form.setFieldsValue({
      user: {
        ...userData,
        email: generatedEmail
      }
    });
  };



  // ✅ Define the single "Personal Information" section
  const sections = [
    {
      title: t("personal_information"),
      content: (
        <div className="row">
          <div className="col-md-4">
            <Form.Item label={t("first_name")} name={["user", "firstName"]} rules={[{ required: true, message: t("first_name_required") }]}>
              <Input placeholder={t("enter_first_name")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("last_name")} name={["user", "lastName"]} rules={[{ required: true, message: t("last_name_required") }]}>
              <Input placeholder={t("enter_last_name")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("gender")} name={["user", "gender"]} rules={[{ required: true, message: t("gender_required") }]}>
              <CommonSelect
                options={translatedGender}
                defaultValue={translatedGender.find(opt => opt.value === form.getFieldValue(["user", "gender"]))}
                onChange={(option) => form.setFieldsValue({ user: { ...form.getFieldsValue().user, gender: option?.value ?? "" } })}
              />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("email")} name={["user", "email"]} rules={[{ required: true, type: "email", message: t("email_required") }]}>
              <Input placeholder={t("enter_email")} addonAfter={
                !isEdit ? (
                  <Button
                    type="text"
                    size="small"
                    onClick={generateEmail}
                    style={{ padding: "4px 8px", height: "auto" }}
                  >
                    {t("generate")}
                    <CustomTooltip content={t("tooltips.generate_email")} />
                  </Button>
                ) : undefined
              } />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("phone_number")} name={["user", "phoneNumber"]} rules={[{ required: true, message: t("phone_number_required") }, {
              validator: (_, value) =>
                value && !isValidPhoneNumber(value)
                  ? Promise.reject(t("validation.phone_number_invalid"))
                  : Promise.resolve(),
            },]}>
              <Input placeholder={t("enter_phone_number")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("massar_code")} name={["massarCode"]} rules={[{ required: true, message: t("massar_code_required") },
            {
              validator: (_, value) =>
                value && !isValidMassarCode(value)
                  ? Promise.reject(t("validation.massar_code_invalid"))
                  : Promise.resolve(),
            },
            ]}>
              <Input placeholder={t("enter_massar_code")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("birth_date")} name={["user", "birthDate"]} rules={[{ required: true, message: t("birth_date_required") }]}>
              <LocalizedDatePicker
                className="form-control datetimepicker"
              />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("city_of_birth")} name={["user", "cityOfBirth"]} rules={[{ required: true, message: t("city_of_birth_required") }]}>
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                placeholder={t("enter_city_of_birth")}
                value={form.getFieldValue(["user", "cityOfBirth"])}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue().user,
                      cityOfBirth: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("address")} name={["user", "address"]} rules={[{ required: true, message: t("address_required") }]}>
              <Input placeholder={t("enter_address")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("zip_code")} name={["user", "zipCode"]} rules={[{ required: true, message: t("zip_code_required") }]}>
              <Input placeholder={t("enter_zip_code")} />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label={t("city")} name={["user", "city"]} rules={[{ required: true, message: t("city_required") }]}>
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                placeholder={t("enter_city")}
                value={form.getFieldValue(["user", "city"])}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue().user,
                      city: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>
          {/* <div className="col-md-4">
            <Form.Item label={t("class")} name="classId" rules={[{ required: false, message: t("class_required") }]} normalize={(value) => value?.value ?? value}>
              <CommonSelect options={classes}  defaultValue={classes.find(opt => opt.value === form.getFieldValue([ "classId"]))}
              />
            </Form.Item>
          </div> */}
        </div>
      ),
    },
    {
      hidden: !ability.can('create', "Guardian"),
      title: t("guardians"),
      content: (
        <div className="row">
          {(isEdit ? displayedGuardians : linkedGuardians).map((guardian, index) => (
            <div key={index} className="col-md-12 mb-3 border-bottom pb-3">
              <div className="row align-items-center">
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-4">
                      <label className="form-label">{t("name")}</label>
                      <Input disabled value={`${guardian.firstName} ${guardian.lastName}`} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t("email")}</label>
                      <Input disabled value={guardian.email} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t("phone")}</label>
                      <Input disabled value={guardian.phoneNumber} />
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-4">
                      <label className="form-label">{t("relationship")}</label>
                      <Input disabled value={t(guardian.relationship)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="text-end mt-3">
            <button className="btn btn-primary" type="button" onClick={() => setAddModalOpen(true)}>
              <i className="ti ti-plus me-1" /> {t("add_new_guardian")}
            </button>
          </div>
        </div>

      )
    }

  ];

  return (
    <>
      <CommonFormLayout
        title={isEdit ? t("edit_student") : t("add_student")}
        form={form}
        onFinish={handleSubmit}
        sections={sections}
        cancelRoute={routes.studentList}
        submitText={t("add_student")}
        isEdit={isEdit}
      />
      <AddGuardianModal
        visible={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={(guardianData) => {
          setAddModalOpen(false);
          if (guardianData) {
            // Add to the list of guardians to be submitted
            setLinkedGuardians(prevLinked => [...prevLinked, guardianData]);
            if (isEdit) {
              // If editing, also add to the displayed list for immediate UI feedback (optimistic update)
              setDisplayedGuardians(prevDisplayed => [...prevDisplayed, guardianData]);
            }
            // If !isEdit, linkedGuardians is the source for display, so it's already updated.
          }
        }}
        studentId={isEdit ? studentId : undefined}
        context="student"
      />

    </>
  );
};

export default StudentForm;
